import {
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  useEventCallback,
} from '@mui/material';
import { memo, useCallback } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import FormGenerality from 'shell/components/shared/form-generality/form';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import Italian from 'shell/components/shared/consult-person/steps/registry-office/generality/italian';
import Foreign from 'shell/components/shared/consult-person/steps/registry-office/generality/foreign';
import ChooseDate from 'shell/components/shared/choice-date';
import { FormikErrors, FormikTouched } from 'formik';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import { AccordionPersonForm } from 'shell/view-models/accordion-person-form';
import ForeignStateList, { ForeignStateVM } from 'shell/components/shared/foreign-state-list';
import { ForeignForm } from 'shell/view-models/foreign-form';
import { ItalianForm } from 'shell/view-models/italian-form';
import { DateTime } from 'luxon';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
import { noop } from 'shell/utils/noop';
import DocumentActForm from 'shell/components/shared/act';
import { AccordionMarriageAnpr05 } from 'shell/utils/consult-person/response-transformer/marital-status/marriage-act-anpr05';
import { AccordionCivilUnionAnpr05 } from 'shell/utils/consult-person/response-transformer/marital-status/civil-union-act-anpr05';

interface FormMarriageProps {
  disabled?: boolean;
  isEditMode?: boolean;
  errors?: FormikErrors<AccordionMarriageAnpr05>;
  touched?: FormikTouched<AccordionMarriageAnpr05>;
  values?: AccordionMarriageAnpr05;
  civilUnionValues?: AccordionCivilUnionAnpr05;
  civilStatus: string;
  onChange: (
    key: keyof AccordionMarriageAnpr05,
    value: AccordionMarriageAnpr05[keyof AccordionMarriageAnpr05]
  ) => void;
  onChanges: (values: AccordionMarriageAnpr05) => void;
}

const FormMarriageComponent = ({
  disabled,
  values,
  onChange,
  onChanges,
  isEditMode,
  errors,
  touched,
  civilUnionValues,
  civilStatus,
}: FormMarriageProps): React.ReactElement => {
  const onFieldPersonChange = useCallback(
    (key, value) => {
      onChange('person', {
        ...(values?.person || {}),
        [key]: value,
      });
    },
    [values?.person, onChange]
  );

  const onFieldGeneralityChange = useCallback(
    (key, value) => {
      onChange('generality', {
        ...(values?.generality || {}),
        [key]: value,
      });
    },
    [values?.generality, onChange]
  );

  const onFieldsGeneralityChange = useCallback(
    (vals) => {
      onChange('generality', {
        ...(values?.generality || {}),
        ...vals,
      });
    },
    [values?.generality, onChange]
  );

  const onFieldItalianChange = useCallback(
    (key, value) => {
      onChange('italian', {
        ...(values?.italian || {}),
        [key]: value,
      });
    },
    [values?.italian, onChange]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChange('foreign', {
        ...(values?.foreign || {}),
        [key]: value,
      });
    },
    [values?.foreign, onChange]
  );

  const handleForeignStateListChange = useEventCallback(
    (citizenship: ForeignStateVM | null | undefined) => {
      onChange('citizenship', citizenship);
    }
  );

  const handleChooseDateChange = useEventCallback(
    (eventDate: DateTime | null, choice?: Array<DatePickerView>) => {
      new Promise((resolve) => resolve(onChange('eventDate', eventDate))).then(() => {
        onChange('eventChoiceDate', choice);
      });
    }
  );

  // const handleMunicipalityListChange = useEventCallback(
  //   (municipality: MunicipalityAnprVM | null) => {
  //     onChange('municipality', municipality);
  //   }
  // );

  const civilUnion = civilStatus === '6';

  // eslint-disable-next-line no-param-reassign
  if (civilUnion) values = civilUnionValues;

  // const isMarriageActDigital = Boolean(
  //   values?.isDigital?.wi?.datiMorteconiuge?.attoEventoANPR?.attoANSC
  // );

  //const isDeathActDigital = Boolean(person.vedovanza?.datiMorteconiuge?.attoEventoANPR?.attoANSC);

  return (
    <Grid container spacing={2}>
      {!civilUnion && (
        <>
          <Grid item xs={12}>
            <PmiTextField
              label="Numero matrimonio"
              aria-label="Numero matrimonio"
              id="marriage-number"
              fullWidth
              value={values?.number || ''}
              disabled={disabled}
              onChange={(event) => {
                onChange('number', event.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <PmiTitleDivider>Dati del coniuge</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <FormGenerality
          person={civilUnion ? civilUnionValues?.person : values?.person}
          generality={civilUnion ? civilUnionValues?.generality : values?.generality}
          onChangePerson={onFieldPersonChange}
          onChangeGenerality={onFieldGeneralityChange}
          onChangeFieldsGenerality={onFieldsGeneralityChange}
          onChangeFieldsPerson={noop}
          disabled={disabled}
          isEditMode={isEditMode}
          errorsGenerality={errors?.generality as FormikErrors<AccordionGeneralityForm>}
          touchedGenerality={
            touched?.generality as unknown as FormikTouched<AccordionGeneralityForm>
          }
          errorsPerson={errors?.person as FormikErrors<AccordionPersonForm>}
          touchedPerson={touched?.person as unknown as FormikTouched<AccordionPersonForm>}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Cittadinanza</PmiTitleDivider>
      </Grid>
      {isEditMode ? null : (
        <Grid item xs={12} md={4}>
          {/** TODO is still present? */}
          <PmiTextField
            label="Codice"
            aria-label="Codice"
            id="citizenship-code"
            fullWidth
            value={values?.citizenshipCode || ''}
            disabled={disabled}
            onChange={(event) => {
              onChange('citizenshipCode', event.target.value);
            }}
          />
        </Grid>
      )}
      <Grid item xs={12} md={4}>
        <ForeignStateList
          label="Cittadinanza"
          aria-label="Cittadinanza"
          id="citizenship"
          value={values?.citizenship || null}
          disabled={isEditMode ? false : disabled}
          onChange={handleForeignStateListChange}
          error={touched?.citizenship && Boolean(errors?.citizenship)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Valido dal"
          aria-label="Valido dal"
          value={values?.from || null}
          onChange={(date) => {
            onChange('from', date);
          }}
          disabled={isEditMode ? false : disabled}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="from"
              fullWidth
              error={touched?.from && Boolean(errors?.from)}
              helperText={errors?.from}
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiTextField
          label="Note"
          aria-label="Note"
          id="note"
          fullWidth
          multiline
          rows={5}
          value={values?.note || ''}
          disabled={isEditMode ? false : disabled}
          error={touched?.note && Boolean(errors?.note)}
          helperText={errors?.note}
          onChange={(event) => {
            onChange('note', event?.target.value);
          }}
          inputProps={{
            maxLength: '240',
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography>{civilUnion ? 'Dati unione civile' : 'Dati matrimonio'}</Typography>
      </Grid>
      <Grid item xs={12} container spacing={2} alignItems="center">
        <ChooseDate
          choice={values?.eventChoiceDate}
          selected={values?.eventDate || null}
          onChange={handleChooseDateChange}
          disabled={disabled}
          label="Data evento"
          error={Boolean(errors?.eventDate) && touched?.eventDate}
          helperText={errors?.eventDate}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Comune</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Evento"
            id="isForeign"
            defaultValue={false}
            value={values?.isForeign}
            onChange={() => onChange('isForeign', !values?.isForeign)}
          >
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Evento Italia"
              disabled={isEditMode ? false : disabled}
            />
            <FormControlLabel
              value="true"
              control={<Radio />}
              label="Evento Stato Estero"
              disabled={isEditMode ? false : disabled}
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      {!values?.isForeign ? (
        <Italian
          disabledComponent={isEditMode ? false : disabled}
          values={values?.italian}
          onChange={onFieldItalianChange}
          errors={errors?.italian as FormikErrors<ItalianForm>}
          birthday={values?.eventDate}
        />
      ) : (
        <Foreign
          disabledComponent={isEditMode ? false : disabled}
          values={values?.foreign}
          onChange={onFieldForeignChange}
          errors={errors?.foreign as FormikErrors<ForeignForm>}
          touched={touched?.foreign as unknown as FormikTouched<ForeignForm>}
        />
      )}
      {/* <Grid item xs={12}>
        <PmiTextField
          label="Luogo eccezionale"
          aria-label="Luogo eccezionale"
          id="exceptional-location"
          fullWidth
          value={values?.exceptionalLocation || ''}
          disabled={isEditMode ? false : disabled}
          error={touched?.exceptionalLocation && Boolean(errors?.exceptionalLocation)}
          helperText={errors?.exceptionalLocation}
          onChange={(event) => {
            onChange('exceptionalLocation', event.target.value);
          }}
          inputProps={{
            maxLength: '120',
          }}
        />
      </Grid> */}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>
          {civilUnion ? 'Atto di unione civile' : 'Atto di matrimonio'}
        </PmiTitleDivider>
      </Grid>
      <DocumentActForm
        values={values}
        onChange={onChange}
        onChanges={onChanges}
        disabled={isEditMode ? false : disabled}
      />
    </Grid>
  );
};

FormMarriageComponent.displayName = 'Form marriage component';

const FormMarriage = memo(FormMarriageComponent);

export default FormMarriage;
